import { FC } from 'react';

import dayjs from 'dayjs';
import { useFormik } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { DateTimePicker } from '@mui/x-date-pickers';
import { PickerValue } from '@mui/x-date-pickers/internals';

import { IEntryRewardForm, validationSchema } from '../form';

import { EntryRewardPicker } from './entryRewardPicker/EntryRewardPicker';

import css from './EntryRewardFormDialog.module.css';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { RichTextarea } from 'v2/components/richTextarea/RichTextarea';
import { UserRewardType } from 'v2/constants';

interface EntryRewardFormProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: IEntryRewardForm;
  isSubmitting: boolean;
  onSubmit: (values: IEntryRewardForm, resetForm: VoidFunction) => void;
}

export const EntryRewardFormDialog: FC<EntryRewardFormProps> = ({
  initialValues,
  isSubmitting,
  onSubmit,
  onClose,
  title,
  isOpen,
}) => {
  const formik = useFormik<IEntryRewardForm>({
    initialValues,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
  });

  const handleChangeDescription = (value: string) => {
    formik.setFieldValue('description', value);
  };

  const handleSelectImage = (fileId: string) => {
    formik.setFieldValue('imageId', fileId);
  };

  const handleChangeDate = (field: keyof IEntryRewardForm) => (value: PickerValue) => {
    formik.setFieldValue(field, value);
  };

  const handleRewardTypeChange = (value: UserRewardType) => {
    formik.setFieldValue('type', value);
    formik.setFieldValue('value', '');
  };

  const handleRewardValueChange = (value: string) => {
    formik.setFieldValue('value', value);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="entryRewardForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
          <TextField
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Title"
            required
          />

          <RichTextarea
            value={formik.values.description}
            label="Description"
            onChange={handleChangeDescription}
            required
          />

          <div className="form-group">
            <DateTimePicker
              className={css.entryRewardFormDatePicker}
              value={formik.values.activationDate}
              onChange={handleChangeDate('activationDate')}
              label="Activation Date *"
              format="DD.MM.YYYY HH:mm"
            />

            <DateTimePicker
              className={css.entryRewardFormDatePicker}
              value={formik.values.expirationDate}
              onChange={handleChangeDate('expirationDate')}
              minDate={dayjs()}
              label="Expiration Date *"
              format="DD.MM.YYYY HH:mm"
            />
          </div>

          <EntryRewardPicker
            type={formik.values.type}
            value={formik.values.value}
            onTypeChange={handleRewardTypeChange}
            onValueChange={handleRewardValueChange}
          />

          <FileSelector
            value={formik.values.imageId}
            onSelect={handleSelectImage}
            error={formik.errors.imageId}
            label="Image"
            required
          />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="entryRewardForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
